/**
 * UTM Attribution Tracking
 * Captures UTM/click ID parameters from the URL on first visit, persists them
 * (sessionStorage + localStorage with 30-day TTL), and provides helpers for
 * signup, checkout, and dashboard analytics.
 */

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const CLICK_ID_KEYS = [
  "fbclid", // Facebook
  "gclid", // Google Ads
  "gbraid", // Google iOS
  "wbraid", // Google web-to-app
  "ttclid", // TikTok
  "msclkid", // Microsoft / Bing
  "li_fat_id", // LinkedIn
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];

export interface AttributionData {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  ttclid?: string | null;
  msclkid?: string | null;
  li_fat_id?: string | null;
  referrer?: string | null;
  landing_page?: string | null;
  first_touch_at?: string | null;
  last_touch_at?: string | null;
}

const STORAGE_KEY = "astrixy_attribution";
const SESSION_KEY = "astrixy_attribution_session";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const safeLocal = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};
const safeSession = () => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const readStored = (): { data: AttributionData; savedAt: number } | null => {
  const ls = safeLocal();
  if (!ls) return null;
  try {
    const raw = ls.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed?.savedAt) return null;
    if (Date.now() - parsed.savedAt > TTL_MS) {
      ls.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeStored = (data: AttributionData) => {
  const ls = safeLocal();
  const ss = safeSession();
  const payload = JSON.stringify({ data, savedAt: Date.now() });
  try {
    ls?.setItem(STORAGE_KEY, payload);
  } catch {
    /* quota / privacy mode */
  }
  try {
    ss?.setItem(SESSION_KEY, payload);
  } catch {
    /* ignore */
  }
};

const parseFromUrl = (search: string): Partial<AttributionData> => {
  const params = new URLSearchParams(search);
  const out: Partial<AttributionData> = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) (out as Record<string, string>)[k] = v.slice(0, 255);
  }
  for (const k of CLICK_ID_KEYS) {
    const v = params.get(k);
    if (v) (out as Record<string, string>)[k] = v.slice(0, 255);
  }
  return out;
};

/**
 * Capture UTM/click params from the current URL. First-touch attribution is
 * preserved; only NEW non-empty values overwrite (last-touch).
 * Returns the merged attribution snapshot.
 */
export const captureAttribution = (): AttributionData => {
  if (typeof window === "undefined") return {};
  const incoming = parseFromUrl(window.location.search);
  const stored = readStored();
  const now = new Date().toISOString();

  const hasIncoming = Object.keys(incoming).length > 0;

  if (!stored) {
    const data: AttributionData = {
      ...incoming,
      referrer: document.referrer || null,
      landing_page: window.location.pathname + window.location.search,
      first_touch_at: now,
      last_touch_at: now,
    };
    writeStored(data);
    return data;
  }

  if (hasIncoming) {
    const merged: AttributionData = {
      ...stored.data,
      ...incoming, // last-touch overwrite
      last_touch_at: now,
    };
    writeStored(merged);
    return merged;
  }

  return stored.data;
};

/** Get the current attribution snapshot without re-capturing. */
export const getAttribution = (): AttributionData => {
  const stored = readStored();
  return stored?.data ?? {};
};

/** Build the metadata object to attach to signup / order / lead inserts. */
export const getAttributionPayload = (): Record<string, string | null> => {
  const a = getAttribution();
  return {
    utm_source: a.utm_source ?? null,
    utm_medium: a.utm_medium ?? null,
    utm_campaign: a.utm_campaign ?? null,
    utm_content: a.utm_content ?? null,
    utm_term: a.utm_term ?? null,
    fbclid: a.fbclid ?? null,
    gclid: a.gclid ?? null,
    ttclid: a.ttclid ?? null,
    msclkid: a.msclkid ?? null,
    referrer: a.referrer ?? null,
    landing_page: a.landing_page ?? null,
    first_touch_at: a.first_touch_at ?? null,
    last_touch_at: a.last_touch_at ?? null,
  };
};

/** Append current UTMs to an outbound URL (e.g., affiliate / checkout link). */
export const appendUtmToUrl = (url: string): string => {
  const a = getAttribution();
  try {
    const u = new URL(url, window.location.origin);
    for (const k of UTM_KEYS) {
      const v = a[k];
      if (v && !u.searchParams.has(k)) u.searchParams.set(k, v);
    }
    return u.toString();
  } catch {
    return url;
  }
};

/** Clear stored attribution (e.g., after successful conversion if desired). */
export const clearAttribution = () => {
  safeLocal()?.removeItem(STORAGE_KEY);
  safeSession()?.removeItem(SESSION_KEY);
};
